import { PropsWithChildren, ReactNode } from 'react';
import { Icon, ICON_SELECTOR, IconName, IconSize } from '@ardoq/icons';
import styled from 'styled-components';
import { colors, spacing } from '@ardoq/design-tokens';
import { header3 } from '@ardoq/typography';
import { IconButton } from '@ardoq/button';
import { TextOverflow } from '@ardoq/popovers';
import { FlexBox } from '@ardoq/layout';

const SidebarMenuTitle = styled(TextOverflow)`
  ${header3};
  align-items: center;
  display: flex;
`;

const SidebarMenuHeaderStyle = styled.div`
  border-bottom: 1px solid ${colors.borderSubtle};
  ${SidebarMenuTitle} {
    color: ${colors.textDefault};
  }
  ${ICON_SELECTOR} {
    color: ${colors.icon};
  }

  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-shrink: 0;

  ${SidebarMenuTitle} {
    gap: ${spacing.s4};
  }
`;

const FlexBoxWithHeight = styled(FlexBox)`
  height: 56px;
`;

type SidebarMenuHeaderProps = {
  title?: string;
  onCloseClick?: () => void;
  dataTestId?: string;
  leftIconName?: IconName;
  leftContent?: ReactNode;
  rightContent?: ReactNode;
  closeButtonLabel?: string;
  closeButtonAriaLabel?: string;
};

export const SidebarMenuHeader = ({
  title,
  onCloseClick,
  children,
  dataTestId,
  leftIconName,
  leftContent,
  rightContent,
  closeButtonLabel = 'Close',
  closeButtonAriaLabel = 'Close sidebar',
}: PropsWithChildren<SidebarMenuHeaderProps>) => {
  return (
    <SidebarMenuHeaderStyle data-test-id={dataTestId}>
      {(title || onCloseClick) && (
        <FlexBoxWithHeight
          justify="space-between"
          width="full"
          padding="medium"
          align="center"
        >
          {title && (
            <SidebarMenuTitle>
              {leftContent}
              {leftIconName && (
                <Icon iconName={leftIconName} iconSize={IconSize.LARGE} />
              )}
              {title}
            </SidebarMenuTitle>
          )}
          <FlexBox gap="xsmall" align="center">
            {rightContent}
            {onCloseClick && (
              <IconButton
                label={closeButtonLabel}
                iconName={IconName.CLOSE}
                onClick={onCloseClick}
                dataTestId="sidebar-menu-header-close-icon"
                aria-label={closeButtonAriaLabel}
              />
            )}
          </FlexBox>
        </FlexBoxWithHeight>
      )}
      {children}
    </SidebarMenuHeaderStyle>
  );
};
